import {
  GenericRequest,
  RequestActionDateUtil,
  RolloutRequestResult,
  ScheduleReferenceDates,
  ScheduleVersionRequestRef,
  SchedulingService,
} from '@csp/csp-common-scheduling';
import { Maybe, Patient, StudyOrg } from '@csp/csp-common-model';
import { getTimeZoneId, dateOrUnixTimeMillisToZonedDateTime } from '@csp/csp-common-date-util';
import { ScheduleReferenceDateService } from '@csp/csp-common-patient-scheduling';
import { VisitRequest } from '../model/VisitRequest';
import { BookedVisit } from '../../model/BookedVisit';
import { VisitRequestTemplateIn } from '../model/VisitRequestTemplateIn';

const getRolloutRequestResultFromVisitRequestIn = async ({
  visitRequestIn,
  request,
  fromRepeatIndex,
  scheduleReferenceDates,
}: {
  visitRequestIn: VisitRequestTemplateIn;
  request: GenericRequest;
  fromRepeatIndex: Maybe<number>;
  scheduleReferenceDates: Maybe<ScheduleReferenceDates>;
}): Promise<RolloutRequestResult> => {
  const startActionTimestamp = RequestActionDateUtil.getStartActionTimestamp(
    visitRequestIn,
    visitRequestIn.scheduleReferenceDates,
  );
  const endActionTimestamp = RequestActionDateUtil.getEndActionTimestamp(
    visitRequestIn,
    visitRequestIn.scheduleReferenceDates,
  );

  // Rollout visit request to see if booking window has changed, e.g by a data change.
  return await SchedulingService.rolloutRequest({
    request,
    startActionTimestamp:
      startActionTimestamp &&
      dateOrUnixTimeMillisToZonedDateTime(startActionTimestamp.unixTimeMillis, visitRequestIn.timezone),
    endActionTimestamp:
      endActionTimestamp &&
      dateOrUnixTimeMillisToZonedDateTime(endActionTimestamp.unixTimeMillis, visitRequestIn.timezone),
    scheduleReferenceDates,
    fromRepeatIndex,
    limitRepeat: 1,
  });
};

const getRolloutRequestResult = async ({
  visit,
  visitRequests,
  patient,
  study,
}: {
  visit: BookedVisit;
  visitRequests: VisitRequest[];
  patient: Patient;
  study: StudyOrg;
}): Promise<RolloutRequestResult> => {
  const visitRequest =
    visitRequests.find(req => ScheduleVersionRequestRef.isSameRequestRef(req, visit.requestRef)) ??
    VisitRequest.toUnknownVisitRequest(visit.requestRef);
  const visitRequestIn = VisitRequestTemplateIn.from(patient, study, [visit.requestRef], getTimeZoneId())(visitRequest);

  return getRolloutRequestResultFromVisitRequestIn({
    visitRequestIn,
    request: visitRequest,
    fromRepeatIndex: visit.requestRef.repeatIndex,
    scheduleReferenceDates: ScheduleReferenceDateService.fromPatient(patient),
  });
};

export const VisitScheduleRolloutService = {
  getRolloutRequestResultFromVisitRequestIn,
  getRolloutRequestResult,
};
